/**
 * THE BANK AND THE CARD, THE SECOND AND THIRD SCREENS OF THE WAYS TO PAY on an invoice's own page,
 * in the owner's order (ops #46): Zelle, then the bank, then the card, one screen at a time. Each is
 * a page of Stripe's that ops makes, so home never sees a card or a bank detail (CLAUDE.md binding
 * rule 6). No artboard draws them (business issue 13), so they are built from drawn parts, the same
 * as Zelle's screen (`Zelle.tsx`): its heading, its sentence and the artboards' buttons, with the
 * seam's words (ops's `docs/seams/home.md`, section 5 at 0b51e49). The words are in `words.ts`.
 *
 * - HER TICK ON THE BANK (section 5, "Her tick", home's Q1): "Charge my bank account automatically
 *   after each clean", unticked whenever the screen is drawn, with ops's `AUTOPAY_BANK_TERMS` beside
 *   it as the tick's description, drawn only while ops offers to save (`offerToSave`). AND ON THE
 *   CARD (ops #70, section 5 at 7e2d7ae, "The card tick", slice M2 of round four), built the same
 *   way: "Charge my card automatically after each clean" with `AUTOPAY_CARD_TERMS`, unticked
 *   whenever the card screen is drawn, whatever the bank's was. Ticked, a card press asks for the
 *   card page with `autopay: true`, which saves the card she pays with; the return page says
 *   nothing of saving after it, its address carrying no `saves`. A tick ops cannot honour is
 *   answered `ready` with the page all the same (`tick_not_honoured`, which the Worker reads as
 *   ready), so she goes to it as after any press. Each tick's paragraph is its own, word for word.
 *   The office's number in it, to call or text to stop the charges, is a link drawn as a
 *   word of it, as the number is in every sentence, the words exactly ops's (slice V3); the address
 *   to email stays its words, since a link on the line under the number would take the taps meant
 *   for it, and so every tap on the number's 48 tall box calls (`e2e/drawn.spec.ts`). Ticked, a
 *   press asks for the bank page with `autopay: true` and the version of the paragraph drawn beside
 *   the tick, `terms_version` (ops's section 12.3, round five, slice A4), the card's the same with
 *   its own; unticked, it sends neither. The paragraphs end with section 12.5's sentence, the Stop
 *   on her account page among the ways to stop. The tick holds still while a press is out, so the
 *   page made is the one she asked for, and a tick put on or taken off makes the next press a new
 *   request. Once a read of the invoice says ops no longer offers it, the tick goes, and is never
 *   sent.
 * - WORDS THAT HAVE CHANGED (ops's section 12.3, slice A4): ops refuses a ticked press `409
 *   terms_changed`, final, where the version it names is not the current one for the page. The
 *   words this tab drew are then not the ones that hold, and the only way to draw those is the page
 *   as the Worker serves it now, so the tab notes the invoice and the way, and loads the page afresh,
 *   the panel up as it goes (`lib/paying.ts`). The page opens on that way (`Invoice.tsx`), and says
 *   ops's sentence above the tick (`TERMS_CHANGED`), with the focus, the tick unticked and the
 *   paragraph as home holds it now, until she presses again or moves to another way.
 * - PRESSING PAY (l.648-675) sends one ask through the request door, `invoice.pay` with the page's
 *   kind and the invoice, and nothing of where Stripe sends her back, which the Worker writes from
 *   its own origin. A key is minted once for a press, and kept only while the door could not be
 *   reached, or asks for a fresh code, so trying again names the same request; anything else the
 *   door answers ends that press, and the next one is a new request. While one is out another press
 *   does nothing.
 * - A FRESH CODE FIRST FOR HER TICK, AND ONLY FOR IT: a ticked press saves the account or the card
 *   to be charged automatically, which changes how the household pays (CLAUDE.md binding rule 5),
 *   so it goes to the door behind a code; when that door answers `403 step_up_required`, the screen
 *   asks for a code in place of Pay, under the tick (`FreshCode.tsx`), as Stop does, saying why in
 *   the tick's words (`BANK.freshCode`, `CARD.tickedFreshCode`), and once she has typed it sends the
 *   same ask again with the same key behind the code. Unticked, the bank page and, since ops's
 *   section 5 at dc63b80, the card page save nothing (the card's no longer carries Stripe's own save
 *   box), so each goes to the plain door with no code, as the contract has home drop it (round five,
 *   slice A4). Taking the tick off takes the code step away, and Pay is back for the plain door.
 *   Once the code has worked, Pay is back in its place whatever the door answers, but a code asked
 *   for again, since the code counts for fifteen minutes.
 * - A TICK OPS NO LONGER OFFERS: the door refuses a tick on an invoice ops last pushed with no offer,
 *   `422 invalid` naming `autopay` (`docs/seam.md` P32), so the screen says it did not go through
 *   and reads the invoice again, which takes the tick away.
 * - THE PANEL OVER THE SCREEN, the moment a press goes, Pay or the code step's own Pay: the screen
 *   below the header dims, the page and the nav go inert (`useCover`, `Shell.tsx`), and a panel in
 *   its middle takes the focus and says, in a live region, "One moment", and that she is being taken
 *   to Stripe to pay; after about eight seconds, that it is taking longer than usual and to keep the
 *   page open. The header stays live above it, Call with it. Built after the owner's walk of
 *   2026-10-16, where only the pointer over the button said anything was happening and the owner
 *   could not tell "if it is broken or slow or what". Zelle asks nothing of anybody, so it never
 *   draws the panel.
 * - ONE PANEL BEHIND A FRESH CODE TOO (slice V3, after the walk was replayed, where the panel said
 *   "Taking you to Stripe" and went, and the code step's Pay was a wait on the button alone). A
 *   press to the door behind a code says "One moment" alone until the door takes the ask, and only
 *   then that she is being taken to Stripe, since the door may ask for a code first, which takes the
 *   panel away for the code step, having promised nothing. The code step's Pay brings the panel up
 *   at once (`FreshCode`'s `onWaiting`), "One moment" while the code is checked, and it stays up,
 *   the one panel, through the ask and the poll until the page comes or the press ends, as a press
 *   with no code does. It goes for the code step only where the code itself ends the press: not
 *   accepted, closed or not gone through, and a code that has run out, each of which the code step
 *   says, with the cursor in its field.
 * - NO PANEL WHERE NO FRESH CODE STANDS, for a ticked press (round four, slice M3, `docs/seam.md`
 *   P59, after the replay found "One moment" drawn over the page and, half a second later, the page
 *   undimmed into the code step, a flash that read as broken). `/api/me` says until when the
 *   session's fresh code holds, and the tab knows one for fifteen minutes from a code typed here or
 *   in her other open tab, which is on the same session (`lib/fresh.ts`). A press behind a code with
 *   none standing draws no panel: Pay is held busy while the door is asked, and the door, which asks
 *   for the code last, answers what it would refuse the press with, said as any press's refusal is,
 *   or `403 step_up_required` only for a press it would take, which draws the code step and tells
 *   the tab none stands; so no code is asked for a press the door would then refuse (round six,
 *   slice S1, after the replay of 2026-10-18 met the door's refusal only after the code). With one
 *   standing it draws the panel and asks the door, as above. A code step drawn within ten minutes of
 *   the last code the tab asked for asks for none (`FreshCode`).
 * - THE POLL asks the Worker where the page stands every three seconds and at most six times, each
 *   ask answered from what the Worker has stored and never waiting on ops (isolation rule 5). Once
 *   the page is ready the tab goes to it, the panel still up, remembering the invoice and the way
 *   she left from, the bank or the card, and never the page's address (`lib/paying.ts`), so the way
 *   back after Stripe's back arrow opens the invoice on that way (slice V3). A browser that brings
 *   this page back from its cache, as the back button can, takes the panel away and offers Pay
 *   again rather than waiting on a press that has gone.
 * - EVERY ASK AND POLL HAS A TIME LIMIT, ten seconds (`REQUEST_LIMIT_MS`, `api.ts`, slice V3): an
 *   ask with no answer by then did not go through, and Try again sends it again with the same key;
 *   a poll with none ends the press there, as not gone through, rather than asking on. So a request
 *   that stalls never leaves the panel waiting with no end.
 * - EVERY OTHER END SAYS WHAT TO DO, in the panel's live region in place of its lines, with the
 *   office's number: no page coming; the poll's budget spent, said by what its last ask said (that
 *   Stripe's page is still being made only where it said so, plainly that it did not go through
 *   where it did not, and that the connection dropped where it never reached the Worker); an ask
 *   that never reached the Worker, as the connection dropped (slice V3, after the replay found a
 *   dropped connection said as Stripe being slow); the door's own limits; or a refusal this screen
 *   has no other answer for. Under it, Try again,
 *   which presses Pay again (not after the door's limits, which ask her to wait), and Close, or
 *   Escape, which gives the screen back with the focus on Pay. The refusals that change what the
 *   ways to pay offer (`Ending`) take the panel away and go to the page, which reads the invoice
 *   again: Stripe off, a bank payment clearing, nothing owed, every page made (the office's number
 *   in place of Pay here, given as `usedUp`), and the saved method paying it.
 * - A SESSION THAT HAS ENDED goes to the door, and an ask or an invoice no longer hers says it did
 *   not go through and reads the invoice again, as "How did we do?" does.
 * - NOTHING IS PAID HERE. An invoice reads paid only once ops pushes it (CLAUDE.md binding rule 1).
 *
 * The panel and the poll are `Leaving.tsx`'s since round five's slice A5, which draws them for the
 * offer to save a way to pay on How you pay too; nothing of them changed in the move.
 */
import { useEffect, useId, useRef, useState, type Ref } from 'react';
import { getPayPage, sendRequest, type AskAnswer, type Door, type TooMany } from '../api.ts';
import { freshStands, noFreshCode } from '../lib/fresh.ts';
import { CLEAR_OF_THE_NAV } from '../lib/motion.ts';
import { away, noteLeaving, noteTermsChanged } from '../lib/paying.ts';
import {
  AUTOPAY_BANK_TERMS,
  AUTOPAY_BANK_TERMS_VERSION,
  AUTOPAY_CARD_TERMS,
  AUTOPAY_CARD_TERMS_VERSION,
  BANK,
  byCard,
  CARD,
  FAILED_BEFORE_PHONE,
  formatCents,
  fromYourBank,
  INVOICE,
  PAYING,
  TERMS_CHANGED,
  tooManyBeforePhone,
  ZELLE,
} from '../words.ts';
import { GHOST_BUTTON, NAVY_BUTTON, TEXT_LINK } from './buttons.ts';
import { FreshCode, type ProvedCame } from './FreshCode.tsx';
import type { Signed } from './Guarded.tsx';
import { LeavingPanel, pollForPage, useLeaving, type PollEnded } from './Leaving.tsx';
import { Phone } from './Phone.tsx';
import { TermsWords } from './Terms.tsx';

/**
 * The refusals that change what the ways to pay offer, in ops's own words (the seam, l.616-620),
 * which the page acts on: Stripe off, a bank payment clearing, nothing owed now, every page from
 * home made, and the saved method paying the invoice.
 */
export type Ending =
  | 'payments_off'
  | 'bank_payment_clearing'
  | 'invoice_not_open'
  | 'pay_pages_used_up'
  | 'saved_method_pays';

export const ENDINGS: readonly Ending[] = [
  'payments_off',
  'bank_payment_clearing',
  'invoice_not_open',
  'pay_pages_used_up',
  'saved_method_pays',
];

/**
 * The version of the paragraph drawn beside each tick, which a ticked press names (ops's section
 * 12.3), so she is recorded under the words she read.
 */
const TERMS_VERSION: Readonly<Record<StripeKind, string>> = {
  bank: AUTOPAY_BANK_TERMS_VERSION,
  card: AUTOPAY_CARD_TERMS_VERSION,
};

/** Ops's refusal of a ticked press whose words are no longer the current ones (section 12.3). */
const TERMS_CHANGED_CODE = 'terms_changed';

/**
 * How the press ended, said in the panel (`Leaving.tsx`): it did not go through; the page still not
 * made when the poll gave up; the connection dropped; or past the request door's own limits (`429
 * too_many`).
 */
type Ended = PollEnded | 'too_many';

/**
 * What the panel says of how a press ended, before the office's number; past the door's own limits,
 * with whose count and when, as the door said them (round six, slice S1).
 */
function endedBeforePhone(line: Ended, limit: TooMany | undefined): string {
  switch (line) {
    case 'slow':
      return PAYING.slowBeforePhone;
    case 'offline':
      return PAYING.connectionDroppedBeforePhone;
    case 'too_many':
      return tooManyBeforePhone(limit);
    default:
      return FAILED_BEFORE_PHONE;
  }
}

/** Which of Stripe's pages the screen asks for. */
export type StripeKind = 'bank' | 'card';

interface StripeWayProps {
  readonly kind: StripeKind;
  readonly invoiceId: string;
  /** The household shown, which the request names. */
  readonly household: string;
  /** What is still to pay, as ops reads it. */
  readonly amountCents: number;
  /**
   * Ops offers to save the method she pays with (`pay.offer_to_save`): the bank and the card each
   * draw her tick.
   */
  readonly offerToSave: boolean;
  /** Ops has made every page it will for the invoice from home: the office's number in place of Pay. */
  readonly usedUp: boolean;
  /** A refusal that changes what the ways to pay offer, for the page to act on. */
  readonly onEnding: (ending: Ending) => void;
  readonly signed: Signed;
  readonly reload: () => void;
  readonly headingRef: Ref<HTMLHeadingElement>;
  /** The bank's way on to the card. */
  readonly onNext?: () => void;
  /** The way back: the bank's to Zelle, absent without one, and the card's to the bank. */
  readonly onBack?: () => void;
  /**
   * The page was loaded afresh on this way because ops said the words beside her tick had changed
   * (`lib/paying.ts`, `noteTermsChanged`): the screen says so above the tick, until she presses.
   */
  readonly termsChanged?: boolean;
  /** Where the line that says so is, for the page to give it the focus as it opens on this way. */
  readonly termsChangedRef?: Ref<HTMLParagraphElement>;
}

export function StripeWay({
  kind,
  invoiceId,
  household,
  amountCents,
  offerToSave,
  usedUp,
  onEnding,
  signed,
  reload,
  headingRef,
  onNext,
  onBack,
  termsChanged = false,
  termsChangedRef,
}: StripeWayProps) {
  const headingId = useId();
  const termsId = useId();
  /** The screen says the words beside the tick changed, as it opened, until she presses. */
  const [changedSaid, setChangedSaid] = useState(termsChanged);
  const usedUpLine = useRef<HTMLParagraphElement>(null);
  const payButton = useRef<HTMLButtonElement>(null);
  /** The door asked for a fresh code, so the code step is drawn in place of Pay. */
  const [coding, setCoding] = useState(false);
  /** Her tick on the bank, unticked whenever the screen is drawn. */
  const [ticked, setTicked] = useState(false);
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  const [limit, setLimit] = useState<TooMany>();
  /**
   * The door is asked about a press behind a code with no fresh code standing, before any code is
   * asked for: Pay is held busy, and no panel is drawn (slice S1).
   */
  const [asking, setAsking] = useState(false);
  /** The panel over the screen, and the press it waits on (`Leaving.tsx`); closed, Pay has the focus. */
  const leaving = useLeaving<Ended>(() => payButton.current?.focus());
  const { waiting, out, wait, finish, begin } = leaving;
  /** The key minted for the press, kept only while the door could not be reached. */
  const minted = useRef<string | null>(null);
  /** She pressed Pay on this screen, so what comes of it takes the focus. */
  const pressed = useRef(false);
  const amount = formatCents(amountCents);
  const bank = kind === 'bank';
  /**
   * The tick is drawn, on the bank and on the card alike (the card's since ops #70), while ops
   * offers to save the method she pays with.
   */
  const offering = offerToSave;
  /** A press asks ops to save the account or the card: the tick is drawn and ticked. */
  const saving = offering && ticked;
  /**
   * A press goes behind a fresh code: with her tick alone, on the bank and the card alike. A card
   * page without it saves nothing since ops's section 5 at dc63b80 (round five, slice A4).
   */
  const behindCode = saving;
  /** Ops's paragraph beside the tick, drawn with the office's number a link (`Terms.tsx`). */
  const termsWords = bank ? AUTOPAY_BANK_TERMS : AUTOPAY_CARD_TERMS;

  // A tick ops no longer offers is taken off, so it is never sent, nor drawn ticked again later; and
  // the code step a tick asked for goes with it.
  if (ticked && !offering) setTicked(false);
  if (coding && !behindCode) setCoding(false);

  // A tick put on or taken off asks for another page, so the next press is a new request.
  useEffect(() => {
    minted.current = null;
  }, [saving]);

  useEffect(() => {
    if (usedUp && pressed.current) usedUpLine.current?.focus();
  }, [usedUp]);

  function refusedWith(code: string | undefined) {
    // The words beside her tick are not the ones that hold: the page as the Worker serves it now
    // draws those, on this way, and says so. The panel stays up as the page goes.
    if (code === TERMS_CHANGED_CODE) {
      noteTermsChanged(invoiceId, kind);
      away.again();
      return;
    }
    const ending = ENDINGS.find((each) => each === code);
    if (ending === undefined) {
      finish('failed');
      // A tick on an invoice ops last pushed with no offer: the invoice is read again, which takes
      // the tick away should ops no longer offer it.
      if (saving && code === 'invalid') reload();
      return;
    }
    finish(null);
    onEnding(ending);
    reload();
  }

  function signedOut() {
    finish(null);
    signed.onSignedOut();
  }

  /**
   * Asks where the page stands, every three seconds and six times at most (`pollForPage`), and acts
   * on what came of it: the tab goes to the page once it is ready, remembering the invoice and the
   * way she left from.
   */
  async function waitForPage(askId: string, current: () => boolean) {
    const polled = await pollForPage(() => getPayPage(invoiceId, askId, household), current);
    switch (polled.outcome) {
      case 'gone':
        return;
      case 'ready':
        noteLeaving(invoiceId, kind);
        away.to(polled.url);
        return;
      case 'signed_out':
        signedOut();
        return;
      case 'not_found':
        finish('failed');
        reload();
        return;
      case 'refused':
        refusedWith(polled.code);
        return;
      case 'ended':
        finish(polled.as);
        return;
    }
  }

  /**
   * Sends the ask for the page to this door, with the key minted for the press, and her tick only
   * where it is ticked, with the version of the paragraph drawn beside it (ops's section 12.3): an
   * unticked tick is not sent at all, nor any version.
   */
  function send(door: Door): Promise<AskAnswer> {
    minted.current ??= crypto.randomUUID();
    return sendRequest(
      {
        key: minted.current,
        type: 'invoice.pay',
        household,
        payload: {
          invoice_id: invoiceId,
          page: kind,
          ...(saving ? { autopay: true, terms_version: TERMS_VERSION[kind] } : {}),
        },
      },
      door,
    );
  }

  /**
   * Acts on what the door answered. The key is let go, so the next press is a new request, unless
   * the door could not be reached or asks for a fresh code first. Taken, the panel says she is being
   * taken to Stripe, as it has since the press where the press needed no code.
   */
  async function answered(answer: AskAnswer, current: () => boolean) {
    if (answer.outcome !== 'failed' && answer.outcome !== 'step_up') minted.current = null;
    switch (answer.outcome) {
      case 'sent':
        wait('going');
        await waitForPage(answer.id, current);
        return;
      case 'step_up':
        // Only a ticked press asks for a code; an unticked one, on the bank or the card, never
        // does, so it did not go through.
        if (!behindCode) {
          finish('failed');
          return;
        }
        // The door decides: no fresh code stands, whatever the tab knew of one.
        noFreshCode();
        finish(null);
        setCoding(true);
        return;
      case 'signed_out':
        signedOut();
        return;
      case 'not_found':
        finish('failed');
        reload();
        return;
      case 'refused':
        refusedWith(answer.code);
        return;
      case 'too_many':
        setLimit(answer.limit);
        finish('too_many');
        return;
      case 'failed':
        finish(answer.offline === true ? 'offline' : 'failed');
        return;
      default:
        finish('failed');
    }
  }

  /**
   * Pay, or Try again in the panel: the panel comes up at once, saying she is being taken to
   * Stripe, or, behind a code, "One moment" alone until the door has taken the ask. Behind a code
   * with no fresh code standing as far as her browser knows (`lib/fresh.ts`), no panel is drawn, so
   * none comes up only to give way to the code step (round four, slice M3, P59): Pay is held busy
   * while the door is asked, which answers what it would refuse the press with, said as any press's
   * refusal is, and `403 step_up_required` only for a press it would take, which draws the code step
   * in place of Pay; so no code is asked for a press the door would then refuse (round six, slice
   * S1). The key minted is kept for the ask behind the code.
   */
  async function press() {
    if (out.current) return;
    pressed.current = true;
    setChangedSaid(false);
    if (behindCode && !freshStands()) {
      const current = begin();
      setAsking(true);
      const answer = await send('billing');
      if (!current()) return;
      setAsking(false);
      await answered(answer, current);
      return;
    }
    const current = begin();
    wait(behindCode ? 'asking' : 'going');
    const answer = await send(behindCode ? 'billing' : 'session');
    if (!current()) return;
    await answered(answer, current);
  }

  /** She puts her tick on or takes it off, unless a press is out, which keeps it as it was sent. */
  function tick(next: boolean) {
    if (out.current) return;
    setTicked(next);
  }

  /**
   * The code step's Pay is pressed: the panel comes up at once, "One moment" while the code is
   * checked, and stays up through the ask that follows (`provedThen`), the one panel.
   */
  function codePressed(pressing: boolean) {
    if (out.current) return;
    if (pressing) {
      pressed.current = true;
      wait('asking');
    } else {
      // The code itself ended the press, which the code step says: the panel goes.
      finish(null);
    }
  }

  /**
   * Once the fresh code works: the same ask again, with the same key, behind the code, the panel
   * still up from the code step's Pay. A code that has run out takes the panel away for the code
   * step, which says so; anything else the door answers goes on as a press does, in the panel, with
   * Pay back in place of the code step, since the code counts for fifteen minutes.
   */
  async function provedThen(): Promise<ProvedCame> {
    if (out.current) return null;
    const current = begin();
    const answer = await send('billing');
    if (!current()) return null;
    if (answer.outcome === 'step_up') {
      finish(null);
      return 'step_up';
    }
    setCoding(false);
    await answered(answer, current);
    return null;
  }

  const panel = (
    <LeavingPanel
      leaving={leaving}
      going={PAYING.takingYou}
      ended={(line) => (
        <>
          {endedBeforePhone(line, limit)} <Phone after="." />
        </>
      )}
      // The door's own limits ask her to try later, so nothing offers to try now.
      retry={(line) => line !== 'too_many'}
      onTryAgain={() => void press()}
    />
  );

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <div>
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="scroll-mt-4 font-serif text-h3 font-semibold text-pretty text-text-body"
        >
          {bank ? BANK.heading : CARD.heading}
        </h2>
        <p className="mt-2 text-body text-pretty">{bank ? fromYourBank(amount) : byCard(amount)}</p>
        {bank ? (
          <p className="mt-2 text-small text-pretty text-text-muted">{ZELLE.samePrice}</p>
        ) : null}
      </div>

      {offering && !usedUp ? (
        <div>
          {/* Ops said the words beside her tick had changed, and the page was loaded afresh to draw
              the ones that hold: said above them, where the page gives the focus as it opens. */}
          {changedSaid ? (
            <p
              ref={termsChangedRef}
              tabIndex={-1}
              className="mb-3 text-body font-semibold text-pretty text-text-body"
            >
              {TERMS_CHANGED}
            </p>
          ) : null}
          {/* Her box stands on the first line of its words, however many lines they wrap to, and
              one line and its box sit centred in the 48 tall target. The paragraph under it starts
              where the words do: the box's 24px and the gap's 12px are its 36px. */}
          <label
            className={`grid min-h-tap grid-cols-[auto_1fr] content-center items-start gap-3 text-body font-semibold text-text-body ${
              waiting ? 'cursor-wait opacity-70' : 'cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              checked={ticked}
              onChange={(event) => tick(event.target.checked)}
              aria-describedby={termsId}
              aria-disabled={waiting ? true : undefined}
              className="mt-1 size-6 cursor-pointer accent-surface-brand aria-disabled:cursor-wait"
            />
            <span className="text-pretty">{bank ? BANK.tick : CARD.tick}</span>
          </label>
          {/* Her tick's description: ops's words as one run of text, read with nothing between
              them. The paragraph below draws the number as a box to tap, and a box beside the
              comma after it is read with a space between them (slice A4). */}
          <span id={termsId} hidden>
            {termsWords}
          </span>
          <TermsWords
            words={termsWords}
            className="mt-1 pl-9 text-small text-pretty wrap-break-word text-text-body"
          />
        </div>
      ) : null}

      {usedUp ? (
        <p ref={usedUpLine} tabIndex={-1} className="text-body text-pretty">
          {INVOICE.payBeforePhone} <Phone after="." />
        </p>
      ) : coding ? (
        <FreshCode
          signedInWith={signed.signedInWith}
          why={bank ? BANK.freshCode : CARD.tickedFreshCode}
          submit={bank ? BANK.pay : CARD.pay}
          closedBeforePhone={bank ? BANK.cannotBeforePhone : CARD.cannotBeforePhone}
          onProved={provedThen}
          onWaiting={codePressed}
        />
      ) : (
        <div>
          <button
            ref={payButton}
            type="button"
            // The button a move between ways brings into view where the way is taller than the
            // screen (`bringScreenToTop`, round six, slice S1), clear of the nav.
            data-pay
            aria-disabled={waiting || asking ? true : undefined}
            onClick={() => void press()}
            className={`cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 ${CLEAR_OF_THE_NAV} ${NAVY_BUTTON}`}
          >
            {bank ? BANK.pay : CARD.pay}
          </button>
        </div>
      )}
      {panel}

      {onNext === undefined ? null : (
        <button type="button" onClick={onNext} className={`cursor-pointer ${GHOST_BUTTON}`}>
          {BANK.cantPayByBank}
        </button>
      )}
      {onBack === undefined ? null : (
        <button type="button" onClick={onBack} className={`cursor-pointer self-start ${TEXT_LINK}`}>
          {bank ? ZELLE.backToZelle : CARD.back}
        </button>
      )}
    </section>
  );
}
